import { BigNumber } from '@ethersproject/bignumber'
import { Trans } from '@lingui/macro'
import { formatNumber, NumberType } from '@x-swap-protocol/conedison/format'
import { CurrencyAmount, Fraction, Percent, Price, Token } from '@x-swap-protocol/sdk-core'
import { Position } from '@x-swap-protocol/v3-sdk'
import RangeBadge from 'components/Badge/RangeBadge'
import { ButtonPurple } from 'components/Button'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import HoverInlineText from 'components/HoverInlineText'
import Loader from 'components/Icons/LoadingSpinner'
import { RowBetween } from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { SupportedChainId } from 'constants/chains'
import { NATIVE_USDC_XDC, WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { useToken } from 'hooks/Tokens'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import { usePool } from 'hooks/usePools'
import useStablecoinPrice from 'hooks/useStablecoinPrice'
import { useUSDPrice } from 'hooks/useUSDPrice'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { IRewardInfo } from 'pages/Farm/interfaces'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Bound } from 'state/mint/v3/actions'
import styled from 'styled-components/macro'
import { HideSmall, MEDIA_WIDTHS, SmallOnly, ThemedText } from 'theme'
import { formatTickPrice } from 'utils/formatTickPrice'
import { unwrappedToken } from 'utils/unwrappedToken'

const LinkRow = styled.div`
  align-items: center;
  display: flex;
  user-select: none;
  flex-direction: column;
  justify-content: space-between;
  color: ${({ theme }) => theme.textPrimary};
  padding: 16px;
  text-decoration: none;
  font-weight: 500;

  & > div:not(:first-child) {
    text-align: center;
  }
  :hover {
    background-color: ${({ theme }) => theme.hoverDefault};
  }

  @media screen and (min-width: ${MEDIA_WIDTHS.deprecated_upToSmall}px) {
    /* flex-direction: row; */
  }

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex-direction: column;
    row-gap: 8px;
  `};
`

const DataLineItem = styled.div`
  font-size: 14px;
`

const RangeComponentWrapper = styled.div`
  width: 200px;
`

const RangeLineItem = styled(DataLineItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
  width: 100%;
`

const DoubleArrow = styled.span`
  font-size: 12px;
  margin: 0 2px;
  color: ${({ theme }) => theme.textTertiary};
`

const RangeText = styled(ThemedText.Caption)`
  font-size: 12px !important;
  word-break: break-word;
  padding: 0.25rem 0.25rem;
  border-radius: 8px;
`

const FeeTierText = styled(ThemedText.UtilityBadge)`
  font-size: 10px !important;
  margin-left: 14px !important;
`
const TokenIdWrapper = styled(Link)`
  cursor: pointer;
  font-size: 10px;
  margin-left: 14px;
  color: #ffffff;
`

const ExtentsText = styled(ThemedText.Caption)`
  color: ${({ theme }) => theme.textTertiary};
  display: inline-block;
  line-height: 16px;
  margin-right: 4px !important;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    display: none;
  `};
`
const HideOnSmall = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    display: none;
  `};
`

const ResponsiveButtonPurple = styled(ButtonPurple)`
  border-radius: 10px;
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  padding: 7px 15px;
  width: fit-content;
  border: none;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex: 1 1 auto;
    width: 100%;
  `};
`

const PrimaryPositionIdData = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  > * {
    margin-right: 8px;
  }
`
const TokenID = styled.div`
  padding: 3px 5px;
  background: ${({ theme }) =>
    theme.darkMode
      ? 'linear-gradient(90deg, #1E28A9 0.78%, #8713D6 100%)'
      : 'linear-gradient(90deg, #534FC7 0%, #AC10AC 100%)'};
  border-radius: 5px;
  font-size: 0.9rem;
`

const ButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 50%;

  @media only screen and (max-width: 390px) {
    bottom: 100%;
  }
`
interface PositionListItemProps {
  token0: string
  token1: string
  tokenId: BigNumber
  fee: number
  liquidity: BigNumber
  tickLower: number
  tickUpper: number
  isStaked: boolean
  chains?: SupportedChainId[]
  rewardInfo?: IRewardInfo
  farmIsEnded?: boolean
  isUnstaked?: boolean
  // methods
  handleUnstake: (tokenId: string) => Promise<void>
  handleStake: (tokenId: string) => Promise<void>
}

export function getPriceOrderingFromPositionForUI(position?: Position): {
  priceLower?: Price<Token, Token>
  priceUpper?: Price<Token, Token>
  quote?: Token
  base?: Token
} {
  if (!position) {
    return {}
  }

  const token0 = position.amount0.currency
  const token1 = position.amount1.currency

  // if token0 is a dollar-stable asset, set it as the quote token
  const stables = [NATIVE_USDC_XDC]
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  const bases = [...Object.values(WRAPPED_NATIVE_CURRENCY)]
  if (bases.some((base) => base && base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0,
  }
}

export default function PositionListItem({
  token0: token0Address,
  token1: token1Address,
  tokenId,
  fee: feeAmount,
  liquidity,
  tickLower,
  tickUpper,
  isStaked,
  handleUnstake,
  handleStake,
  rewardInfo,
  farmIsEnded,
  isUnstaked,
}: PositionListItemProps) {
  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  const price0 = useStablecoinPrice(token0 ?? undefined)
  const price1 = useStablecoinPrice(token1 ?? undefined)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined
  const rewardCurrency = rewardInfo && rewardInfo?.token ? unwrappedToken(rewardInfo.token) : undefined
  const rewardUSD = useUSDPrice(tryParseCurrencyAmount(rewardInfo?.current, rewardCurrency))

  // construct Position from details returned
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, feeAmount)

  const position = useMemo(() => {
    if (pool) {
      return new Position({
        pool,
        liquidity: liquidity.toString(),
        tickLower,
        tickUpper,
      })
    }
    return undefined
  }, [liquidity, pool, tickLower, tickUpper])

  const tickAtLimit = useIsTickAtLimit(feeAmount, tickLower, tickUpper)

  // prices
  const { priceLower, priceUpper, quote, base } = getPriceOrderingFromPositionForUI(position)

  const currencyQuote = quote && unwrappedToken(quote)
  const currencyBase = base && unwrappedToken(base)

  // check if price is within range
  const outOfRange: boolean = pool ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper : false

  const removed = liquidity?.eq(0)

  const fiatValueOfLiquidity: CurrencyAmount<Token> | null = useMemo(() => {
    if (!price0 || !price1 || !position) return null
    const amount0 = price0.quote(position.amount0)
    const amount1 = price1.quote(position.amount1)
    return amount0.add(amount1)
  }, [price0, price1, position])

  const positionSummaryLink = '/pools/' + tokenId

  const reward = useMemo(() => {
    if (!rewardInfo || !Number(rewardInfo.max)) {
      return {
        current: '-',
        inUSD: '',
        percent: '-',
      }
    }
    return {
      current: `${Number(rewardInfo.current).toFixed(3)} ${rewardInfo.token.symbol}`,
      inUSD: `(${formatNumber(rewardUSD.data, NumberType.FiatTokenStats)})`,
      percent: `${((Number(rewardInfo.current) / Number(rewardInfo.max)) * 100).toFixed(0)}%`,
    }
  }, [rewardInfo, rewardUSD])

  return (
    <LinkRow>
      <RowBetween>
        <PrimaryPositionIdData>
          <DoubleCurrencyLogo currency0={currencyBase} currency1={currencyQuote} size={18} margin />
          <HideOnSmall>
            <ThemedText.SubHeader>
              &nbsp;{currencyQuote?.symbol}&nbsp;/&nbsp;{currencyBase?.symbol}
            </ThemedText.SubHeader>

            <FeeTierText>
              <Trans>{new Percent(feeAmount, 1_000_000).toSignificant()}%</Trans>
            </FeeTierText>
          </HideOnSmall>

          <TokenIdWrapper to={positionSummaryLink}>
            <MouseoverTooltip text={<Trans>Token ID</Trans>}>
              <TokenID>{tokenId.toString()} ↗</TokenID>
            </MouseoverTooltip>
          </TokenIdWrapper>
        </PrimaryPositionIdData>
        <RangeComponentWrapper>
          <RangeBadge removed={removed} inRange={!outOfRange} />
        </RangeComponentWrapper>
      </RowBetween>
      <RangeLineItem>
        <RangeText>
          <ExtentsText>
            <Trans>Liquidity: </Trans>
          </ExtentsText>

          {fiatValueOfLiquidity?.greaterThan(new Fraction(1, 100)) ? (
            // <ThemedText.DeprecatedLargeHeader fontSize="36px" fontWeight={500}>
            //   <Trans>${fiatValueOfLiquidity.toFixed(2, { groupSeparator: ',' })}</Trans>
            // </ThemedText.DeprecatedLargeHeader>
            <Trans>
              <span>${fiatValueOfLiquidity.toFixed(2, { groupSeparator: ',' })}</span>
            </Trans>
          ) : (
            <Trans>
              <span>$-</span>
            </Trans>
          )}
        </RangeText>
      </RangeLineItem>

      {isStaked && rewardInfo && (
        <RangeLineItem>
          <RangeText>
            <ExtentsText>
              <Trans>Reward: </Trans>
            </ExtentsText>
            <Trans>{Number(rewardInfo.current) ? <span>{`${reward.current} ${reward.inUSD}`}</span> : '-'}</Trans>
          </RangeText>{' '}
          <RangeText>
            <MouseoverTooltip
              text={
                <Trans>
                  Percent of Vesting period for the position being in range. In case of claiming before reaching 100%
                  will not allow claim the rest of rewards
                </Trans>
              }
            >
              <ExtentsText>
                <Trans>Vested:</Trans>
              </ExtentsText>
              <Trans>
                <span
                  style={{ color: (Number(rewardInfo.current) / Number(rewardInfo.max)) * 100 < 100 ? 'orange' : '' }}
                >
                  {reward.percent}
                </span>
              </Trans>
            </MouseoverTooltip>
          </RangeText>
        </RangeLineItem>
      )}

      {priceLower && priceUpper ? (
        <RowBetween style={{ position: 'relative' }}>
          <RangeLineItem>
            <RangeText>
              <ExtentsText>
                <Trans>Min: </Trans>
              </ExtentsText>
              <Trans>
                <span>
                  {formatTickPrice({
                    price: priceLower,
                    atLimit: tickAtLimit,
                    direction: Bound.LOWER,
                  })}{' '}
                </span>
                <HoverInlineText text={currencyQuote?.symbol} /> per{' '}
                <HoverInlineText text={currencyBase?.symbol ?? ''} />
              </Trans>
            </RangeText>{' '}
            <HideSmall>
              <DoubleArrow>↔</DoubleArrow>{' '}
            </HideSmall>
            <SmallOnly>
              <DoubleArrow>↔</DoubleArrow>{' '}
            </SmallOnly>
            <RangeText>
              <ExtentsText>
                <Trans>Max:</Trans>
              </ExtentsText>
              <Trans>
                <span>
                  {formatTickPrice({
                    price: priceUpper,
                    atLimit: tickAtLimit,
                    direction: Bound.UPPER,
                  })}{' '}
                </span>
                <HoverInlineText text={currencyQuote?.symbol} /> per{' '}
                <HoverInlineText maxCharacters={10} text={currencyBase?.symbol} />
              </Trans>
            </RangeText>
          </RangeLineItem>

          <ButtonWrapper>
            {isStaked ? (
              <ResponsiveButtonPurple onClick={() => handleUnstake(tokenId.toString())}>
                <Trans>{isUnstaked ? 'Reward & Claim' : 'Unstake'}</Trans>
              </ResponsiveButtonPurple>
            ) : (
              <ResponsiveButtonPurple disabled={farmIsEnded} onClick={() => handleStake(tokenId.toString())}>
                <Trans>Stake</Trans>
              </ResponsiveButtonPurple>
            )}
          </ButtonWrapper>
        </RowBetween>
      ) : (
        <Loader />
      )}
    </LinkRow>
  )
}
